import React, { useState, useEffect, useRef } from "react";
import { ticketsProps } from "./apis/getTickets";
import { claimPattern, entryptedData } from "./apis/claimPattern";
import { drawId } from "./urlParams";
import { remainingPatternsProps } from "./BingoPage";
import useWindowSize from "./utils/useWindowSize";

interface BingoPageProps {
  ticketData: ticketsProps | undefined;
  drawDetails: any;
  calledNumbers: (number | string)[];
  autoHighlightPattern: boolean;
  remainingPatterns?: remainingPatternsProps;
  isMobile?: boolean;
  handleClaimAnimation: (status: boolean) => void;
}

interface GridItem {
  gridItems: number[][];
  game: { gridSize: string };
  id: string;
}

interface TicketDimensions {
  ticketWidth: number;
  ticketHeight: number;
  cellSize: number;
  colsCount: number;
  rowsCount: number;
}

interface WinningResult {
  patternType: string;
  isWinning: boolean;
  positions: [number, number][];
}
type gridSizeTypes = "5x5" | "5x10";

function calculateTicketDimensions(
  gridSize: string,
  cellSize: number
): TicketDimensions {
  const [rows, cols] = gridSize.split("x").map(Number);
  const ticketWidth = cols * cellSize;
  const ticketHeight = rows * cellSize;
  return {
    ticketWidth,
    ticketHeight,
    cellSize,
    colsCount: cols,
    rowsCount: rows,
  };
}

export default function BingoTickets({
  ticketData,
  handleClaimAnimation,
  isMobile,
  remainingPatterns,
  drawDetails,
  autoHighlightPattern,
  calledNumbers,
}: BingoPageProps) {
  const [selectedCells, setSelectedCells] = useState<{
    [key: string]: boolean;
  }>({});
  const [markedPositions, setMarkedPositions] = useState<{
    [ticketId: string]: [number, number][];
  }>({});
  const [winningTickets, setWinningTickets] = useState<{
    [ticketId: string]: WinningResult[];
  }>({});
  const [highlightedPositions, setHighlightedPositions] = useState<{
    [ticketId: string]: [number, number][];
  }>({});
  const { width, height } = useWindowSize();
  const claimedPatternsRef = useRef<Set<string>>(new Set());
  const failedPatternClaimsRef = useRef<Set<string>>(new Set());
  const setHighlightedInLocalStorage = (data: any) => {
    localStorage.setItem("highlightedPatterns", JSON.stringify(data));
  };



  const isNumberCalled = (number: (number | string)) => (calledNumbers.includes(number) || calledNumbers.includes(String(number)))

  useEffect(() => {
    if (drawId) {
      const storedCells = localStorage.getItem(`selectedCells_${drawId}`);
      // if (storedCells) {
      const parsedCells = storedCells ? JSON.parse(storedCells) : "";
      setSelectedCells(parsedCells);
      // }
      const storedClaims = localStorage.getItem(`claimedPatterns_${drawId}`);
      const markedPositions = JSON.parse(
        localStorage.getItem(`markedPositions`) || `{}`
      );
      const highlightedPositions = JSON.parse(
        localStorage.getItem(`highlightedPatterns`) || `{}`
      );
      const parsedClaims = JSON.parse(storedClaims || "{}");
      setHighlightedPositions(highlightedPositions);
      setMarkedPositions(markedPositions);
      if (Object.keys(parsedClaims)?.length > 0) {
        const newWinningTickets: { [ticketId: string]: WinningResult[] } = {};
        const newMarkedPositions: { [ticketId: string]: [number, number][] } =
          {};

        Object.entries(parsedClaims).forEach(
          ([ticketId, claims]: [string, any]) => {
            const winningResults: WinningResult[] = claims.map(
              (claim: any) => ({
                patternType: claim.markedPatternName,
                isWinning: true,
                positions: claim.markedPositions,
              })
            );

            newWinningTickets[ticketId] = winningResults;
            newMarkedPositions[ticketId] = winningResults.flatMap(
              (result) => result.positions
            );

            winningResults.forEach((result) => {
              const patternKey = `${ticketId}-${result.patternType
                }-${JSON.stringify(result.positions.sort())}`;
              claimedPatternsRef.current.add(patternKey);
            });
          }
        );

        setWinningTickets(newWinningTickets);

        if (ticketData?.rows) {
          const updatedCells = { ...parsedCells };
          ticketData.rows.forEach((ticket: GridItem, ticketIndex: number) => {
            const { gridSize } = ticket.game;
            const [rows, cols] = gridSize.split("x").map(Number);

            newMarkedPositions[ticket.id]?.forEach(([row, col]) => {
              const cellIndex = row * cols + col;
              const cellKey = `${ticketIndex}-${cellIndex}`;
              updatedCells[cellKey] = true;
            });
          });
          setSelectedCells(updatedCells);
        }
      }
    }
  }, [drawId, ticketData]);

  const checkRemainingCounts =
    remainingPatterns !== undefined && remainingPatterns.totalRemaining > 0
      ? remainingPatterns
      : undefined;

  useEffect(() => {
    if (
      !autoHighlightPattern ||
      !ticketData?.rows ||
      !calledNumbers?.length ||
      (remainingPatterns && remainingPatterns.totalRemaining === 0)
    )
      return;

    const autoMarkAndHighlight = async () => {
      const newMarkedPositions: { [ticketId: string]: [number, number][] } = {};
      const newSelectedCells: { [key: string]: boolean } = {};
      const newHighlightedPositions: {
        [ticketId: string]: [number, number][];
      } = {};

      ticketData.rows.forEach((ticket: GridItem, ticketIndex: number) => {
        const ticketId = ticket.id;
        const { gridSize } = ticket.game;
        const [rows, cols] = gridSize.split("x").map(Number);

        ticket.gridItems.flat().forEach((number: number, cellIndex: number) => {
          // if (calledNumbers !== undefined && calledNumbers.includes(number)) {
          if (calledNumbers !== undefined && isNumberCalled(number)) {
            const row = Math.floor(cellIndex / cols);
            const col = cellIndex % cols;
            const cellKey = `${ticketIndex}-${cellIndex}`;

            newSelectedCells[cellKey] = true;
            if (!newMarkedPositions[ticketId]) {
              newMarkedPositions[ticketId] = [];
            }
            newMarkedPositions[ticketId].push([row, col]);
          }
        });

        const prizes = drawDetails.draw.prizes;
        for (let patternType in prizes) {
          const patternData = prizes[patternType];

          if (
            remainingPatterns !== undefined &&
            remainingPatterns.totalRemaining > 0 &&
            remainingPatterns.patterns[patternType] <= 0
          ) {
            continue;
          }

          if (!patternData?.pattern) continue;

          const patterns = Array.isArray(patternData.pattern)
            ? patternData.pattern
            : [patternData.pattern];

          patterns.forEach((pattern: any) => {
            const patternPositions: [number, number][] = pattern.filter(
              (pos: any) => Array.isArray(pos) && pos.length === 2
            ) as [number, number][];

            // const canBeHighlighted = patternPositions.every(([r, c]) => {
            //     return calledNumbers !==undefined && calledNumbers.includes(ticket.gridItems[r][c]);
            // });

            const canBeHighlighted = patternPositions.every(([r, c]) => {
              const number = ticket?.gridItems?.[r]?.[c];
              return (
                number === null ||
                (calledNumbers !== undefined && isNumberCalled(number))
              );
            });

            const patternKey = `${ticketId}-${patternType}-${JSON.stringify(
              patternPositions.sort()
            )}`;
            const isPatternAlreadyClaimed =
              claimedPatternsRef.current.has(patternKey);
            const isPatternFailed =
              failedPatternClaimsRef.current.has(patternKey);

            if (
              canBeHighlighted &&
              !isPatternAlreadyClaimed &&
              !isPatternFailed
            ) {
              if (!newHighlightedPositions[ticketId]) {
                newHighlightedPositions[ticketId] = [];
              }

              patternPositions.forEach((pos) => {
                const [r, c] = pos;
                const cellNumber = ticket?.gridItems?.[r]?.[c];
                // if (!newHighlightedPositions[ticketId].some(([r, c]) => r === pos[0] && c === pos[1])) {
                //     newHighlightedPositions[ticketId].push(pos);
                // }
                if (
                  cellNumber !== null &&
                  !newHighlightedPositions[ticketId].some(
                    ([r2, c2]) => r2 === r && c2 === c
                  )
                ) {
                  newHighlightedPositions[ticketId].push(pos);
                }
              });
            }
          });
        }
      });
      setSelectedCells(newSelectedCells);
      setMarkedPositions(newMarkedPositions);
      setHighlightedPositions(newHighlightedPositions);
      setHighlightedInLocalStorage(newHighlightedPositions);
    };
    autoMarkAndHighlight();
  }, [calledNumbers, ticketData, autoHighlightPattern, remainingPatterns]);

  useEffect(() => {
    if (drawId && Object.keys(selectedCells).length > 0) {
      localStorage.setItem(
        `selectedCells_${drawId}`,
        JSON.stringify(selectedCells)
      );
    }
  }, [selectedCells, drawId]);
  const checkWinningPattern = async (
    drawDetails: any,
    checkP: number[][],
    ticketId: string
  ): Promise<WinningResult[]> => {
    if (!checkP || checkP.length === 0) return [];

    const prizes = drawDetails.draw.prizes;
    if (!prizes) return [];

    const results: WinningResult[] = [];
    const positionsMatch = (pos1: number[], pos2: number[]): boolean =>
      pos1[0] === pos2[0] && pos1[1] === pos2[1];
    const isExactPatternMatch = (
      pattern: number[][],
      selectedPositions: number[][]
    ): boolean =>
      pattern.every((patternPos) =>
        selectedPositions.some((selectedPos) =>
          positionsMatch(patternPos, selectedPos)
        )
      );

    for (let patternType in prizes) {
      const patternData = prizes[patternType];
      if (!patternData || !patternData.pattern) continue;

      const patterns = Array.isArray(patternData.pattern)
        ? patternData.pattern
        : [patternData.pattern];
      for (let pattern of patterns) {
        const patternPositions: number[][] = pattern.filter(
          (pos: any) => Array.isArray(pos) && pos.length === 2
        );

        if (
          patternPositions.length > 0 &&
          isExactPatternMatch(patternPositions, checkP)
        ) {
          const positions = patternPositions as [number, number][];
          const patternKey = `${ticketId}-${patternType}-${JSON.stringify(
            positions.sort()
          )}`;

          if (claimedPatternsRef.current.has(patternKey)) {
            results.push({
              patternType: patternType,
              isWinning: true,
              positions: positions,
            });
          }
        }
      }
    }
    return results;
  };

  const checkAllTicketsForWinningPatterns = async () => {
    const results: { [ticketId: string]: WinningResult[] } = {};

    for (const ticketId in markedPositions) {
      if (markedPositions[ticketId] && markedPositions[ticketId].length > 0) {
        const matchResults = await checkWinningPattern(
          drawDetails,
          markedPositions[ticketId],
          ticketId
        );
        if (matchResults && matchResults.length > 0) {
          results[ticketId] = matchResults;
        }
      }
    }

    return results;
  };

  useEffect(() => {
    const updateWinningTickets = async () => {
      const results = await checkAllTicketsForWinningPatterns();
      setWinningTickets((prev) => ({ ...prev, ...results }));
    };
    updateWinningTickets();
  }, [markedPositions]);

  if (!ticketData || !ticketData.rows || ticketData.rows.length === 0) {
    return <div>No ticket data available.</div>;
  }

  const firstTicket = ticketData.rows[0];

  const calculateCellSize = (
    gridSize: gridSizeTypes,
    ticketsCount: number
  ): number => {
    let baseSize = 45;

    if (gridSize == "5x5") {
      if (ticketsCount == 1 || ticketsCount == 2) {
        baseSize = 80;
      } else if (ticketsCount <= 4) {
        baseSize = 70;
      }
    } else if (gridSize == "5x10") {
      if (ticketsCount === 1 || ticketsCount === 2) {
        baseSize = 40;
      } else if (ticketsCount <= 3) {
        baseSize = 35;
      } else {
        baseSize = 33;
      }
    }
    if (gridSize === "5x5") {
      if (width < 320) {
        return Math.max(12, Math.round(baseSize * 0.3));
      } else if (width < 375) {
        return Math.max(16, Math.round(baseSize * 0.34));
      } else if (width < 425) {
        return Math.max(16, Math.round(baseSize * 0.4));
      } else if (width < 640) {
        return Math.max(25, Math.round(baseSize * 0.5));
      } else if (width < 768) {
        return Math.max(30, Math.round(baseSize * 0.5));
      } else if (width < 1024) {
        return Math.max(35, Math.round(baseSize * 0.7));
      } else if (width < 1280) {
        return Math.max(35, Math.round(baseSize * 0.6));
      } else if (width < 1600) {
        return Math.min(50, Math.round(baseSize * 0.7));
      } else if (width > 1600) {
        return Math.min(70, Math.round(baseSize * 1.5));
      } else {
        return Math.min(60, Math.round(baseSize * 1.5));
      }
    } else {
      if (width < 640) {
        return Math.max(25, Math.round(baseSize * 0.5));
      } else if (width < 768) {
        return Math.max(30, Math.round(baseSize * 0.5));
      } else if (width < 1024) {
        return Math.max(35, Math.round(baseSize * 0.6));
      } else if (width < 1280) {
        return Math.max(35, Math.round(baseSize * 0.6));
      } else if (width < 1600) {
        return Math.min(50, Math.round(baseSize * 1.2));
      } else if (width > 1600) {
        return Math.min(70, Math.round(baseSize * 1.5));
      } else {
        return Math.min(60, Math.round(baseSize * 1.5));
      }
    }
  };

  const getFontSize = (gridSize: gridSizeTypes) => {
    if (gridSize === "5x5") {
      if (width <= 320) {
        return "text-[10px]";
      } else if (width <= 375) {
        return "text-[10px]";
      } else if (width <= 425) {
        return "text-[11px]";
      } else if (width <= 768) {
        return "text-[14px]";
      } else if (width <= 1024) {
        return "text-[24px]";
      } else if (width <= 1440) {
        return "text-[18px]";
      } else {
        return "text-[20px]";
      }
    } else {
      if (width <= 320) {
        return "text-[11px]";
      } else if (width <= 375) {
        return "text-[10px]";
      } else if (width <= 425) {
        return "text-[9px]";
      } else if (width <= 768) {
        return "text-[14px]";
      } else if (width <= 1024) {
        return "text-[16px]";
      } else if (width <= 1440) {
        return "text-[18px]";
      } else {
        return "text-[20px]";
      }
    }
  };

  const cellSize = calculateCellSize(
    firstTicket.game.gridSize as gridSizeTypes,
    ticketData.rows.length
  );
  const textSize = getFontSize(firstTicket.game.gridSize as gridSizeTypes);

  const { ticketWidth, ticketHeight, colsCount } = calculateTicketDimensions(
    firstTicket.game.gridSize,
    cellSize
  );



  const checkAndClaimPatterns = async (
    ticketId: string,
    currentPositions: number[][],
    ticketIndex: number
  ) => {
    const ticket = ticketData.rows.find((t) => t.id === ticketId);

    if (!ticket) return;

    const prizes = drawDetails.draw.prizes;
    // Initialize with the current highlighted positions
    const newHighlightedPositions = { ...highlightedPositions };

    // Make sure the ticketId property exists
    if (!newHighlightedPositions[ticketId]) {
      newHighlightedPositions[ticketId] = [];
    }

    for (let patternType in prizes) {
      if (
        remainingPatterns !== undefined &&
        remainingPatterns.totalRemaining > 0 &&
        remainingPatterns.patterns[patternType] <= 0
      ) {
        continue;
      }

      const patternData = prizes[patternType];
      if (!patternData || !patternData.pattern) continue;

      const patterns = Array.isArray(patternData.pattern)
        ? patternData.pattern
        : [patternData.pattern];

      for (let pattern of patterns) {
        const patternPositions: [number, number][] = pattern.filter(
          (pos: any) => Array.isArray(pos) && pos.length === 2
        ) as [number, number][];

        if (patternPositions.length === 0) continue;

        // Check if this pattern might be a match with the current positions
        const isPatternComplete = patternPositions.every(([r, c]) => {
          const number = ticket?.gridItems?.[r]?.[c];
          // Check if this cell is marked or is null (free space)
          return (
            number === null ||
            currentPositions.some(([mr, mc]) => mr === r && mc === c)
          );
        });

        if (isPatternComplete) {
          // Additional check to see if all called numbers in this pattern are actually called
          const canBeHighlighted = patternPositions.every(([r, c]) => {
            const number = ticket?.gridItems?.[r]?.[c];
            return (
              number === null ||
              (calledNumbers !== undefined && isNumberCalled(number))
            );
          });

          const patternKey = `${ticketId}-${patternType}-${JSON.stringify(
            patternPositions.sort()
          )}`;
          const isPatternAlreadyClaimed =
            claimedPatternsRef.current.has(patternKey);
          const isPatternFailed =
            failedPatternClaimsRef.current.has(patternKey);

          if (
            canBeHighlighted &&
            !isPatternAlreadyClaimed &&
            !isPatternFailed
          ) {
            // Add each non-null position to highlighted positions
            patternPositions.forEach(([r, c]) => {
              const cellNumber = ticket?.gridItems?.[r]?.[c];
              if (
                cellNumber !== null &&
                !newHighlightedPositions[ticketId].some(
                  ([r2, c2]) => r2 === r && c2 === c
                )
              ) {
                newHighlightedPositions[ticketId].push([r, c]);
              }
            });
          }
        }
      }
    }

    // Important: Actually set the state!
    setHighlightedPositions(newHighlightedPositions);
    setHighlightedInLocalStorage(newHighlightedPositions);
  };
  const handleNumberClick = async (
    ticketIndex: number,
    cellIndex: number,
    number: number,
    ticketId: string
  ) => {
    const ticket = ticketData.rows[ticketIndex];
    const { gridSize } = ticket.game;
    const [rows, cols] = gridSize.split("x").map(Number);
    const row = Math.floor(cellIndex / cols);
    const col = cellIndex % cols;
    const cellKey = `${ticketIndex}-${cellIndex}`;
    const isHighlighted = highlightedPositions[ticketId]?.some(
      ([r, c]) => r === row && c === col
    );
    const isAlreadySelected = selectedCells[cellKey];

    if (isHighlighted) {
      const prizes = drawDetails.draw.prizes;
      let patternsToClaim: WinningResult[] = [];

      for (let patternType in prizes) {
        if (
          remainingPatterns !== undefined &&
          remainingPatterns.totalRemaining > 0 &&
          remainingPatterns.patterns[patternType] <= 0
        ) {
          continue;
        }

        const patternData = prizes[patternType];
        if (!patternData || !patternData.pattern) continue;

        const patterns = Array.isArray(patternData.pattern)
          ? patternData.pattern
          : [patternData.pattern];

        for (let pattern of patterns) {
          const patternPositions: [number, number][] = pattern.filter(
            (pos: any) => Array.isArray(pos) && pos.length === 2
          ) as [number, number][];

          if (patternPositions.some(([r, c]) => r === row && c === col)) {
            const canBeClaimed = patternPositions.every(([r, c]) => {
              const number = ticket?.gridItems?.[r]?.[c];
              return (
                number === null ||
                (calledNumbers !== undefined && isNumberCalled(number))
              );
            });

            const patternKey = `${ticketId}-${patternType}-${JSON.stringify(
              patternPositions.sort()
            )}`;
            const isPatternAlreadyClaimed =
              claimedPatternsRef.current.has(patternKey);
            const isPatternFailed =
              failedPatternClaimsRef.current.has(patternKey);

            if (canBeClaimed && !isPatternAlreadyClaimed && !isPatternFailed) {
              patternsToClaim.push({
                patternType,
                isWinning: true,
                positions: patternPositions,
              });
            }
          }
        }
      }

      if (patternsToClaim.length > 0) {
        let tempMarkedPositions = {};

        setMarkedPositions((prev) => {
          const currentPositions = [...(prev[ticketId] || [])];
          const newPositions = [...currentPositions];

          patternsToClaim.forEach((pattern) => {
            pattern.positions.forEach((pos) => {
              if (
                !newPositions.some(([r, c]) => r === pos[0] && c === pos[1])
              ) {
                newPositions.push(pos);
              }
            });
          });
          tempMarkedPositions = {
            ...prev,
            [ticketId]: newPositions,
          };
          localStorage.setItem(
            "markedPositions",
            JSON.stringify(tempMarkedPositions)
          );
          return tempMarkedPositions;
        });

        setSelectedCells((prev) => {
          const updated = { ...prev };
          patternsToClaim.forEach((pattern) => {
            pattern.positions.forEach(([r, c]) => {
              const idx = r * cols + c;
              const cellKey = `${ticketIndex}-${idx}`;
              updated[cellKey] = true;
            });
          });
          return updated;
        });

        for (const pattern of patternsToClaim) {
          try {
            const claimData: entryptedData = {
              ticketId: ticketId,
              markedPatternName: pattern.patternType,
              calledBalls: calledNumbers,
              markedPositions: pattern.positions,
            };

            const response = await claimPattern(claimData);
            const patternKey = `${ticketId}-${pattern.patternType
              }-${JSON.stringify(pattern.positions.sort())}`;

            if (response && response.data.success) {
              claimedPatternsRef.current.add(patternKey);

              handleClaimAnimation(true);

              setWinningTickets((prev) => {
                const updated = {
                  ...prev,
                  [ticketId]: [...(prev[ticketId] || []), pattern],
                };

                const storedClaims = localStorage.getItem(
                  `claimedPatterns_${drawId}`
                );
                const claims = storedClaims ? JSON.parse(storedClaims) : {};
                claims[ticketId] = claims[ticketId] || [];
                claims[ticketId].push(claimData);
                localStorage.setItem(
                  `claimedPatterns_${drawId}`,
                  JSON.stringify(claims)
                );

                return updated;
              });

              setHighlightedPositions((prev) => {
                const updated = { ...prev };
                if (updated[ticketId]) {
                  updated[ticketId] = updated[ticketId].filter(
                    ([r, c]) =>
                      !pattern.positions.some(
                        ([pr, pc]) => pr === r && pc === c
                      )
                  );
                  if (updated[ticketId].length === 0) delete updated[ticketId];
                }
                setHighlightedInLocalStorage(updated);
                return updated;
              });
            } else {
              failedPatternClaimsRef.current.add(patternKey);

              setHighlightedPositions((prev) => {
                const updated = { ...prev };
                if (updated[ticketId]) {
                  updated[ticketId] = updated[ticketId].filter(
                    ([r, c]) =>
                      !pattern.positions.some(
                        ([pr, pc]) => pr === r && pc === c
                      )
                  );
                  if (updated[ticketId].length === 0) delete updated[ticketId];
                }
                setHighlightedInLocalStorage(updated);
                return updated;
              });
            }
          } catch (error) {
            console.error(
              `Failed to claim pattern ${pattern.patternType}:`,
              error
            );
            const patternKey = `${ticketId}-${pattern.patternType
              }-${JSON.stringify(pattern.positions.sort())}`;
            failedPatternClaimsRef.current.add(patternKey);

            setHighlightedPositions((prev) => {
              const updated = { ...prev };
              if (updated[ticketId]) {
                updated[ticketId] = updated[ticketId].filter(
                  ([r, c]) =>
                    !pattern.positions.some(([pr, pc]) => pr === r && pc === c)
                );
                if (updated[ticketId].length === 0) delete updated[ticketId];
              }
              setHighlightedInLocalStorage(updated);
              return updated;
            });
          }
        }

        return;
      }
    }

    if (isNumberCalled(number)) {
      if (isAlreadySelected) {
        return;
      }

      setSelectedCells((prev) => ({
        ...prev,
        [cellKey]: true,
      }));
      let tempMarkedPositions = {};
      setMarkedPositions((prev) => {
        const currentPositions = [...(prev[ticketId] || [])];

        if (!currentPositions.some(([r, c]) => r === row && c === col)) {
          tempMarkedPositions = {
            ...prev,
            [ticketId]: [...currentPositions, [row, col]],
          };
          localStorage.setItem(
            "markedPositions",
            JSON.stringify(tempMarkedPositions)
          );
          return tempMarkedPositions;
        }

        return prev;
      });

      setTimeout(async () => {
        const updatedPositions = [
          ...(markedPositions[ticketId] || []),
          [row, col],
        ];
        await checkAndClaimPatterns(ticketId, updatedPositions, ticketIndex);
      }, 0);
    }
  };

  const claimPatternAndUpdateState = async (
    pattern: WinningResult,
    ticketId: string,
    calledNumbers: number[]
  ) => {
    const patternKey = `${ticketId}-${pattern.patternType}-${JSON.stringify(
      pattern.positions.sort()
    )}`;

    if (
      !claimedPatternsRef.current.has(patternKey) &&
      !failedPatternClaimsRef.current.has(patternKey)
    ) {
      try {
        const claimData: entryptedData = {
          ticketId: ticketId,
          markedPatternName: pattern.patternType,
          calledBalls: calledNumbers,
          markedPositions: pattern.positions,
        };

        handleClaimAnimation(true);

        const response = await claimPattern(claimData);

        if (response && response.data.success) {
          claimedPatternsRef.current.add(patternKey);

          setWinningTickets((prev) => {
            const updated = {
              ...prev,
              [ticketId]: [...(prev[ticketId] || []), pattern],
            };

            const storedClaims = localStorage.getItem(
              `claimedPatterns_${drawId}`
            );
            const claims = storedClaims ? JSON.parse(storedClaims) : {};
            claims[ticketId] = claims[ticketId] || [];
            claims[ticketId].push(claimData);
            localStorage.setItem(
              `claimedPatterns_${drawId}`,
              JSON.stringify(claims)
            );

            return updated;
          });
        } else {
          failedPatternClaimsRef.current.add(patternKey);

          setHighlightedPositions((prev) => {
            const updated = { ...prev };
            if (updated[ticketId]) {
              updated[ticketId] = updated[ticketId].filter(
                ([r, c]) =>
                  !pattern.positions.some(([pr, pc]) => pr === r && pc === c)
              );
              if (updated[ticketId].length === 0) delete updated[ticketId];
            }
            setHighlightedInLocalStorage(updated);
            return updated;
          });
        }
      } catch (error) {
        failedPatternClaimsRef.current.add(patternKey);

        setHighlightedPositions((prev) => {
          const updated = { ...prev };
          if (updated[ticketId]) {
            updated[ticketId] = updated[ticketId].filter(
              ([r, c]) =>
                !pattern.positions.some(([pr, pc]) => pr === r && pc === c)
            );
            if (updated[ticketId].length === 0) delete updated[ticketId];
          }
          setHighlightedInLocalStorage(updated);
          return updated;
        });

        console.error(`Failed to claim pattern ${pattern.patternType}:`, error);
      }
    }
  };

  const isCellSelected = (ticketIndex: number, cellIndex: number) => {
    const cellKey = `${ticketIndex}-${cellIndex}`;
    return selectedCells[cellKey] || false;
  };

  const isUnclaimedHighlighted = (
    ticketId: string,
    row: number,
    col: number
  ) => {
    if (!highlightedPositions[ticketId]) return false;

    const isInCurrentHighlightPattern = highlightedPositions[ticketId].some(
      ([r, c]) => r === row && c === col
    );

    if (!isInCurrentHighlightPattern) return false;
    // ----------------
    const ticket = ticketData.rows.find((ticket) => ticket.id === ticketId)!;
    const currentNumber = ticket.gridItems[row][col];
    // Don't highlight null cells
    if (currentNumber === null) return false;

    const prizes = drawDetails.draw.prizes;
    for (let patternType in prizes) {
      const patternData = prizes[patternType];
      if (!patternData?.pattern) continue;

      if (
        remainingPatterns !== undefined &&
        remainingPatterns.totalRemaining > 0 &&
        remainingPatterns.patterns[patternType] <= 0
      ) {
        continue;
      }

      const patterns = Array.isArray(patternData.pattern)
        ? patternData.pattern
        : [patternData.pattern];

      for (let pattern of patterns) {
        const patternPositions: [number, number][] = pattern.filter(
          (pos: any) => Array.isArray(pos) && pos.length === 2
        ) as [number, number][];

        if (patternPositions.some(([r, c]) => r === row && c === col)) {
          const { gridSize } = ticketData.rows.find(
            (ticket) => ticket.id === ticketId
          )!.game;
          const [rows, cols] = gridSize.split("x").map(Number);
          const ticket = ticketData.rows.find(
            (ticket) => ticket.id === ticketId
          )!;

          const canBeHighlighted = patternPositions.every(([r, c]) => {
            const number = ticket?.gridItems?.[r]?.[c];
            // return calledNumbers !== undefined && calledNumbers.includes(ticket.gridItems[r][c]);
            return (
              number === null ||
              (calledNumbers !== undefined && isNumberCalled(number))
            );
          });

          const patternKey = `${ticketId}-${patternType}-${JSON.stringify(
            patternPositions.sort()
          )}`;
          const isPatternAlreadyClaimed =
            claimedPatternsRef.current.has(patternKey);
          const isPatternFailed =
            failedPatternClaimsRef.current.has(patternKey);

          if (
            canBeHighlighted &&
            !isPatternAlreadyClaimed &&
            !isPatternFailed
          ) {
            return true;
          }
        }
      }
    }

    return false;
  };

  return (
    <div className="flex   flex-wrap  justify-center  md:gap-6 gap-2 lg:p-4">
      {ticketData.rows.map((ticket: GridItem, ticketIndex: number) => {
        const ticketId = ticket.id;
        const winningPatternsForTicket = winningTickets[ticketId] || [];
        const allWinningPositions: [number, number][] =
          winningPatternsForTicket.flatMap((pattern) => pattern.positions);

        return (
          <div
            key={ticketIndex}
            className="relative overflow-hidden"
            style={{
              width: `${ticketWidth + 16}px`,
              height: `${ticketHeight + 16}px`,
              backgroundImage: `url('/assets/ticketBg.png')`,
              backgroundSize: "100% 100%",
              backgroundRepeat: "no-repeat",
            }}
          >
            <div
              className="grid absolute top-2 left-2"
              style={{
                gridTemplateColumns: `repeat(${colsCount}, ${cellSize}px)`,
              }}
            >
              {ticket.gridItems
                .flat()
                .map((number: number, cellIndex: number) => {
                  const { gridSize } = ticket.game;
                  const [rows, cols] = gridSize.split("x").map(Number);
                  const row = Math.floor(cellIndex / cols);
                  const col = cellIndex % cols;

                  const isWinningPosition = allWinningPositions.some(
                    ([r, c]) => r === row && c === col
                  );
                  const isSelected = isCellSelected(ticketIndex, cellIndex);
                  const shouldShake = isUnclaimedHighlighted(
                    ticketId,
                    row,
                    col
                  );

                  return (
                    <div
                      key={cellIndex}
                      className="flex items-center justify-center relative cursor-pointer"
                      style={{
                        width: `${cellSize}px`,
                        height: `${cellSize}px`,
                      }}
                      onClick={() =>
                        handleNumberClick(
                          ticketIndex,
                          cellIndex,
                          number,
                          ticketId
                        )
                      }
                    >
                      {shouldShake ? (
                        <div className="relative w-full h-full flex items-center justify-center animate-shake-up-down">
                          <img
                            className="w-full h-full object-contain"
                            src="/assets/bluBgWithStar.png"
                            alt="bluBgWithStar"
                          />
                          <span className="absolute text-white font-bold text-sm z-10">
                            {number}
                          </span>
                        </div>
                      ) : isWinningPosition && number !== null ? (
                        <div className="relative  w-full h-full flex items-center justify-center">
                          <img
                            className="w-full h-full object-contain"
                            src="/assets/bluBgWithStar.png"
                            alt="bluBgWithStar"
                          />
                          <span className="absolute text-white font-bold text-sm z-10">
                            {number}
                          </span>
                        </div>
                      ) : isSelected && number !== null ? (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <img
                            src="/assets/blueStar.png"
                            alt="Blue Star"
                            className="w-full h-full object-contain"
                          />
                          <span className="absolute text-white font-bold text-sm z-10">
                            {number}
                          </span>
                        </div>
                      ) : (
                        <div
                          className={`flex items-center  justify-center text-black ${textSize} font-bold w-full h-full`}
                          style={{
                            backgroundImage: `url('/assets/numBg.png')`,
                            backgroundSize: "100% 100%",
                            backgroundRepeat: "no-repeat",
                          }}
                        >
                          {number}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
